import { UsageToegangService } from '../../services/usage-toegang.service';
import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core';
import { StorageUrlService } from 'src/app/services/storage-url.service';
import { CommonModule } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { GgImageDirective } from '../../directives/gg-image.directive';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../services/auth.service';
import { IOmgeving, IUser } from '../../interfaces/user.interface';
import { of, startWith } from 'rxjs';
import { IRole } from '../../interfaces/role.interface';
import { RoleEnum } from '../../enums/role.enum';
import { StorageLabelComponent } from '../storage-label/storage-label.component';
import { PermissionService } from '../../services/permission.service';
import { Permission } from '../../enums/permission.enum';
import { CdkMenu, CdkMenuItem, CdkMenuTrigger } from '@angular/cdk/menu';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { Dialog } from '@angular/cdk/dialog';
import { TeamsDialogComponent } from '../teams-dialog/teams-dialog.component';
import { TeamsService } from '../../services/teams.service';
import { OnboardingService } from '../../services/onboarding.service';
import { beheertLeden } from '../../helpers/leden';
import { Overlay } from '@angular/cdk/overlay';
import { ClubKiezerComponent, ClubKiezerData } from '../club-kiezer/club-kiezer.component';
import { isTelefoon } from '../../helpers/telefoon';

@Component({
  selector: 'app-account-dropdown',
  standalone: true,
  imports: [CommonModule, BsDropdownModule, GgImageDirective, RouterLink, RouterLinkActive, SvgIconComponent, TranslateModule, StorageLabelComponent, CdkMenuTrigger, CdkMenu, CdkMenuItem],
  templateUrl: './account-dropdown.component.html',
  styleUrls: ['./account-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountDropdownComponent {
  /** De pagina geeft de knop zelf mee (ng-content); alleen het menu komt van hier. */
  @Input() eigenKnop = false;

  private storageUrlService = inject(StorageUrlService);
  authService = inject(AuthService);
  router = inject(Router);
  permissionService = inject(PermissionService);
  private dialog = inject(Dialog);
  private teamsService = inject(TeamsService);
  private onboarding = inject(OnboardingService);
  private overlay = inject(Overlay);

  public get storageUrl(): string {
    return this.storageUrlService.base;
  }
  public user: IUser = this.authService.user;
  public avatar$ = this.authService.avatarUpdated$.pipe(
    startWith(this.user.avatar),
    map(response => this.storageUrl + response)
  );

  public isClubAdmin = this.checkIsSuperAdmin(this.user.role);
  /** Usage: de clubbeheerder, of iemand met het vinkje "Can see usage". */
  public usageZien$ = inject(UsageToegangService).magZien();
  /** Users: club_admin, of teambeheerder van minstens één team. */
  public beheertLeden = beheertLeden(this.user);

  public kijktInLeague = this.authService.kijktInLeague;
  public alleenKijken = this.authService.alleenKijken;
  public draagtBijAanLeague = this.authService.draagtBijAanLeague;
  /** Een club die een super-admin opende: dan staat "Switch club" in het menu. */
  public beheerderInClub = this.authService.isBeheerderInClub;
  /** Een super-admin die als deze gebruiker meekijkt ("Open as user", alleen kijken). */
  public meekijken = this.authService.meekijken;

  /** Eigen club en league-omgevingen; leeg als het ophalen mislukt. */
  public omgevingen$ = this.authService.omgevingen().pipe(
    catchError(() => of([] as IOmgeving[])),
    shareReplay(1)
  );

  public wisselt = false;

  public clubOmgevingen(omgevingen: IOmgeving[]): IOmgeving[] {
    return omgevingen.filter((o) => o.kind !== 'league');
  }

  public leagueOmgevingen(omgevingen: IOmgeving[]): IOmgeving[] {
    return omgevingen.filter((o) => o.kind === 'league');
  }

  public heeftLeague(omgevingen: IOmgeving[]): boolean {
    return this.leagueOmgevingen(omgevingen).length > 0;
  }

  public inLeagueOmgeving(omgevingen: IOmgeving[]): boolean {
    return this.leagueOmgevingen(omgevingen).some((o) => o.active);
  }

  /**
   * League-menu naast Clubs: wissel naar de (eerste) league-omgeving als je
   * daar nog niet in zit, en open daarna club-albums — zelfde functie als
   * Clubs, andere omgeving.
   */
  public onOpenLeague(omgevingen: IOmgeving[]): void {
    const leagues = this.leagueOmgevingen(omgevingen);
    const actief = leagues.find((o) => o.active);
    if (actief) {
      void this.router.navigateByUrl('/club-albums');
      return;
    }
    const doel = leagues[0];
    if (!doel || this.wisselt) return;
    this.wisselt = true;
    this.authService.wisselOmgeving(doel.club).subscribe({
      next: () => {
        void this.router.navigateByUrl('/club-albums');
      },
      error: () => {
        this.wisselt = false;
      },
    });
  }

  public onWissel(omgeving: IOmgeving): void {
    if (omgeving.active || this.wisselt) {
      return;
    }
    this.wisselt = true;
    this.authService.wisselOmgeving(omgeving.club).subscribe({
      error: () => this.wisselt = false
    });
  }

  /** Een speler (club_player): geen clubcijfers zoals de opslag. */
  public readonly speler = this.permissionService.isSpeler();

  permissions = {
    // Het menu-item Panels alleen voor wie panelen mag bewerken (admin en analist).
    eventSchema: this.permissionService.hasPermission(Permission.EVENTS_EDIT_SCHEMA) && environment.permissions.eventsShowSchema,
    usersShow: environment.permissions.usersShow
  };

  private checkIsSuperAdmin(role: IRole | string): boolean {
    if (typeof role === 'string') {
      return role === RoleEnum.CLUB_ADMIN;
    } else {
      return role.key === RoleEnum.CLUB_ADMIN;
    }
  }

  /** Hoeveel ploegen er aanstaan; null als het er geen zijn. */
  public aantalPloegen(): number | null {
    return this.teamsService.teams?.length || null;
  }

  public onTeams(): void {
    this.dialog.open(TeamsDialogComponent);
  }

  /**
   * Het kiesvenster (clubkiezer, 24-10-2026). Op de telefoon (ook liggend)
   * een blad van onderen, verder in beeld net onder de kopregel.
   */
  public onWisselClub(): void {
    const telefoon = isTelefoon();
    const plek = this.overlay.position().global().centerHorizontally();
    this.dialog.open<void, ClubKiezerData>(ClubKiezerComponent, {
      data: { sluitbaar: true },
      maxWidth: '100vw',
      positionStrategy: telefoon ? plek.bottom('0') : plek.top('12vh'),
      autoFocus: false,
    });
  }

  /** Help -> "Getting started". */
  public onAanDeSlag(): void {
    this.onboarding.openAanDeSlag();
  }

  /**
   * Stoppen met meekijken: uitloggen en het tabblad sluiten dat de
   * beheerconsole opende. Lukt sluiten niet (zelf geopend tabblad), dan naar
   * het inlogscherm.
   */
  public onStopMeekijken(): void {
    this.authService.logout();
    window.close();
    this.router.navigateByUrl('/auth/login').then(() => window.location.reload());
  }

  public onLogout(): void {
    this.authService.logout();
    this.router.navigateByUrl('/auth/login').then(() => {
      window.location.reload();
    });
  }
}
